// Members section of the invite panel: the first five members of the current
// context (the org, or the project inside the canvas), each with a name, an
// email and a role, plus "All members" → /team-and-agents.
//
// It loads its OWN list when it mounts (it used to wait for the header
// button to preload it onto the root before the panel could open, so the
// panel showed nothing until a network round trip ended). Three states, each
// in its own box: loading (three skeleton rows), error (a line and a retry),
// ready (the rows; "Only you so far" when nobody else is there yet).
//
// A row's role is EITHER a select (when this caller may change it, to the
// roles the server accepts — `invitePolicy().editRolesFor`) OR a static,
// translated label. A row never shows an empty right side.
export const MembersInInvite = {
  // FA204 — every helper the handlers read travels on `scope`.
  scope: {
    MAX_VISIBLE: 5,

    // Context-adaptive source: project members inside a project, the active
    // org's members (sdk.listOrgMembers) everywhere else. `key` is a data key
    // with a prefix, so it can never read as an HTML tag name.
    async load(host, s) {
      const st = s && typeof s.update === 'function' ? s : host.state
      const root = (st && st.root) || host.getRootState() || {}
      const projectId = root.projectId
      const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
      // The first load starts in 'loading' already (the declared state) — no
      // write while the panel is still being created; a retry sets it here.
      if (st.membersStatus !== 'loading') st.update({ membersStatus: 'loading' })
      try {
        let raw = []
        if (projectId) raw = (await host.call('getProjectMembers')) || []
        else if (orgId) raw = (await host.sdk.listOrgMembers(orgId)) || []
        const members = raw.map((v) => {
          const u = (v && v.user) || v || {}
          const eff = (v && v.effectiveRole) || {}
          const id = String((v && (v._id || v.id)) || u._id || u.id || u.email || '')
          return {
            key: 'member_' + id,
            // The membership row id the PATCH routes key on — not the user id.
            memberId: (v && (v._id || v.id)) || '',
            name: u.name || '',
            username: u.username || '',
            email: u.email || '',
            avatar: u.avatar || '',
            role: eff.key || (v && v.role) || u.role || '',
            roleName: eff.name || '',
            tier: eff.baseTier || (v && v.role) || u.role || ''
          }
        })
        st.update({ members, membersStatus: 'ready' })
      } catch (err) {
        console.warn('[MembersInInvite] load failed:', err && err.message ? err.message : err)
        st.update({ members: [], membersStatus: 'error' })
      }
    },

    // The translated name of a role, or the custom role's own name.
    roleText(el, s) {
      const key = el.call('invitePolicy', s).labelKey(s.role)
      return key ? el.call('polyglot', key) : s.roleName || s.role || ''
    }
  },

  state: { members: [], membersStatus: 'loading' },
  flow: 'y',
  gap: 'Y',
  onInit: (el, s) => {
    el.scope.load(el, s)
  },

  CaptionTitle: {
    Text: {
      text: (el, s) =>
        s.membersStatus === 'ready'
          ? el
              .call('polyglot', 'members.invite.membersCount')
              .replace('{count}', String((s.members || []).length))
          : el.call('polyglot', 'members.invite.members')
    }
  },

  Loading: {
    flow: 'y',
    ariaBusy: 'true',
    hide: (el, s) => s.membersStatus !== 'loading',
    children: [{}, {}, {}],
    childProps: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      padding: 'Y 0',
      SkeletonCircle: { boxSize: 'B', flexShrink: '0' },
      SkeletonRow: { width: '50%' }
    }
  },

  LoadError: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    hide: (el, s) => s.membersStatus !== 'error',
    Message: {
      tag: 'span',
      role: 'alert',
      fontSize: 'Z1',
      color: 'caption',
      text: (el, s) =>
        s.membersStatus === 'error' ? el.call('polyglot', 'members.invite.loadFailed') : ''
    },
    AppCapsuleBtnGhost: {
      compact: true,
      text: '{{ members.invite.retry | polyglot }}',
      onClick: (ev, el, s) => {
        ev.preventDefault()
        const host = el.lookup('MembersInInvite')
        if (host) host.scope.load(host, host.state)
      }
    }
  },

  List: {
    flow: 'y',
    hide: (el, s) => s.membersStatus !== 'ready',
    children: (el, s) => (s.members || []).slice(0, el.lookup('MembersInInvite').scope.MAX_VISIBLE),
    childrenAs: 'state',
    childProps: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      padding: 'Y 0',
      minWidth: '0',
      // The person's own picture, else their initials. (A `key` factory here
      // never ran — the framework Avatar seeds from the element key, so every
      // row painted the same "Avatar" face.) The name is in the row's text,
      // so the image is decorative.
      Avatar: {
        boxSize: 'B',
        flexShrink: '0',
        src: (el, s) => s.avatar || el.call('avatarUrl', s.name || s.username || s.email),
        alt: ''
      },
      Who: {
        flow: 'y',
        flex: '1',
        minWidth: '0',
        MemberName: {
          tag: 'span',
          fontSize: 'Z1',
          color: 'title',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => s.name || s.username || s.email
        },
        MemberEmail: {
          tag: 'span',
          fontSize: 'Y1',
          color: 'caption',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          hide: (el, s) => !s.email || s.email === (s.name || s.username),
          text: (el, s) => s.email
        }
      },

      // The editable role — only where the server accepts a change from
      // this caller (invitePolicy). Org: PATCH /organizations/:orgId/
      // members/:id. Project: PATCH /projects/:projectId/members/:id.
      InlineSelect: {
        hide: (el, s) => !el.call('invitePolicy', s).editRolesFor(s).length,
        aria: { label: (el) => el.call('polyglot', 'members.inviteForm.roleOfMember') },
        value: (el, s) => s.role,
        onChange: async (ev, el, s) => {
          const value = ev.target.value
          const prev = s.role
          if (!value || value === prev) return
          const root = (s && s.root) || el.getRootState() || {}
          const policy = el.call('invitePolicy', s)
          const t = (k) => el.call('polyglot', k)
          // Optimistic: the select already shows the new role; the model
          // follows it now and goes back on a refusal (the select's `value`
          // reads the model, so the revert repaints it).
          s.update({ role: value })
          try {
            if (policy.context === 'project') {
              await el.sdk.updateMemberRole(root.projectId, s.memberId, value)
            } else {
              const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
              await el.sdk.updateOrgMember(orgId, s.memberId, { role: value })
            }
            s.update({ tier: value })
            const roleKey = policy.labelKey(value)
            el.call('openNotification', {
              title: t('members.roleUpdated.title'),
              message: t('members.roleUpdated.message')
                .replace('{name}', s.name || s.username || t('members.roleUpdated.fallbackName'))
                .replace('{role}', roleKey ? t(roleKey) : value),
              type: 'success'
            })
          } catch (err) {
            s.update({ role: prev })
            el.call('openNotification', {
              title: t('members.role.changeFailed'),
              message: `${err && err.message ? err.message : err}`,
              type: 'error'
            })
            console.warn('[MembersInInvite] role change failed:', err)
          }
        },
        childrenAs: 'props',
        childProps: {
          tag: 'option',
          text: (el) => el.call('polyglot', el.labelKey),
          attr: { selected: (el, s) => (el.value === s.role ? '' : null) }
        },
        children: (el, s) => el.call('invitePolicy', s).editRolesFor(s)
      },

      // The role as text, for every row this caller may not change.
      RoleText: {
        tag: 'span',
        flexShrink: '0',
        fontSize: 'Z1',
        color: 'caption',
        hide: (el, s) =>
          !!el.call('invitePolicy', s).editRolesFor(s).length ||
          !el.lookup('MembersInInvite').scope.roleText(el, s),
        text: (el, s) => el.lookup('MembersInInvite').scope.roleText(el, s)
      }
    }
  },

  OnlyYou: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    color: 'caption',
    hide: (el, s) => {
      // Read before any early return, so the signal is always tracked.
      const me = String(s.root.currentUserEmail || '').toLowerCase()
      if (s.membersStatus !== 'ready') return true
      return !(s.members || []).every((m) => me && String(m.email || '').toLowerCase() === me)
    },
    text: '{{ members.invite.onlyYou | polyglot }}'
  },

  // The full roster ("All members") — /team-and-agents owns member
  // management. A link, not a button (it navigates); it closes the panel
  // first. Bare `Link` key (FA806): the framework Link is the `<a>`.
  Link: {
    href: '/team-and-agents',
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    // The row's wash reaches Z into the panel's A padding, so its text lines
    // up with the member rows above it and the wash stays inside the panel.
    margin: '- -Z',
    padding: 'Y Z',
    round: 'radiusControl',
    fontSize: 'Z1',
    fontWeight: '500',
    color: 'title',
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': { background: 'hover' },
    ':active': { background: 'selected' },
    ':focus-visible': { shadow: 'focusRingInset' },
    Text: { text: '{{ members.invite.allMembers | polyglot }}' },
    Icon: { name: 'arrowAngleRight', boxSize: 'iconSm', flexShrink: '0' },
    onClick: (ev, el) => {
      ev.preventDefault()
      ev.stopPropagation()
      el.call('closeDropdown', 'appInvite')
      el.router('/team-and-agents', el.getRoot())
    }
  }
}
